import {
	Component,
	EventEmitter,
	Optional,
	Output,
	ViewChild,
} from '@angular/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppTagConfiguratorComponent } from '@datachain/ui-sdk/components/app-tag-configurator';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { OrderedSet } from 'immutable';
import {
	debounceTime,
	filter,
	merge,
	Observable,
	of,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { configuration } from '../../../core/configuration';
import { ValidationErrorKeys } from '../../../ui/form/validation-erros-keys';
import {
	SubscriptionDiagnosticError,
	SubscriptionErrorKey,
} from '../../domain/diagnostic-errors.entity';
import { PublicMetadataEntity } from '../../domain/public-metadata.entity';
import { SubscriptionProvider } from '../../providers/subscription.provider';
import {
	PublicMetadataForm,
	PublicMetadataFormControls,
} from './public-metadata.form';

@Component({
	selector: 'app-public-metadata-config',
	templateUrl: './public-metadata-config.component.html',
	styleUrls: ['./public-metadata-config.component.scss'],
	inputs: ['publicMetadata', 'isInViewMode'],
	providers: [PublicMetadataForm],
})
export class PublicMetadataConfigComponent extends DcBaseComponent {
	protected readonly ValidationErrorKeys = ValidationErrorKeys;
	protected readonly Configuration = configuration;

	public Appearance: MatFormFieldAppearance = 'legacy';
	public PublicMetadataFormControls = PublicMetadataFormControls;

	@ViewChild(AppTagConfiguratorComponent)
	public tagConfiguratorComponentCmp: AppTagConfiguratorComponent | null = null;

	@Output()
	public hasPublicMetadataChanged = new EventEmitter<void>();

	public vo$: Observable<{
		publicMetadata: PublicMetadataEntity;
		isInViewMode: boolean;
	}>;

	public constructor(
		@Optional()
		private readonly subscriptionProvider: SubscriptionProvider,
		public readonly publicMetadataForm: PublicMetadataForm
	) {
		super();
		this.cmpId = 'public-metadata-config';

		this.toObservable<PublicMetadataEntity>('publicMetadata')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((config) => {
					this.publicMetadataForm.populate(config);
				})
			)
			.subscribe();

		this.publicMetadataForm.form.valueChanges
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(300),
				withLatestFrom(this.toObservable<boolean>('isInViewMode')),
				filter(([, isInViewMode]) => !isInViewMode),
				tap(() => {
					if (!this.publicMetadataForm.form.valid) {
						this.subscriptionProvider.addCriticalError(
							$localize`:i18n=@@subscription.diagnosticError.invalidPublicMetadata:`,
							SubscriptionErrorKey.InvalidPublicMetadata
						);
					} else {
						this.subscriptionProvider.removeError(
							SubscriptionErrorKey.InvalidPublicMetadata
						);
					}
					this.hasPublicMetadataChanged.emit();
				})
			)
			.subscribe();

		this.vo$ = combineAll({
			publicMetadata: this.toObservable<PublicMetadataEntity>('publicMetadata'),
			isInViewMode: merge(
				of(false),
				this.toObservable<boolean>('isInViewMode')
			),
		});
	}

	public hasKeyWordsChanged(): void {
		this.hasPublicMetadataChanged.emit();
	}

	public async extractFormData(): Promise<PublicMetadataEntity> {
		let keywords = new Array<AppTagItemEntity>();
		if (this.tagConfiguratorComponentCmp) {
			keywords = await this.tagConfiguratorComponentCmp.getUsedTags();
		}
		const ownMetadata = this.publicMetadataForm.extract();
		return PublicMetadataEntity.build({
			title: ownMetadata?.title,
			description: ownMetadata?.description,
			keywords: OrderedSet(keywords),
		});
	}
}
